/**
 * Локализация без зависимостей. Язык выбирается так: сохранённый выбор из
 * настроек → язык Telegram → язык браузера. Русскоязычные локали (ru, be, kk)
 * получают русский, остальные английский.
 */
import { useSyncExternalStore } from 'react';
import type { Entry, Lang, Plural } from './define';
import common from './messages/common';
import editor from './messages/editor';
import result from './messages/result';
import progress from './messages/progress';
import projects from './messages/projects';
import balance from './messages/balance';
import invite from './messages/invite';
import profile from './messages/profile';
import support from './messages/support';
import components from './messages/components';
import lib from './messages/lib';
import labels from './messages/labels';

export type { Lang } from './define';

const MESSAGES = {
  ...common, ...editor, ...result, ...progress, ...projects, ...balance,
  ...invite, ...profile, ...support, ...components, ...lib, ...labels,
} satisfies Record<string, Entry>;

export type MessageKey = keyof typeof MESSAGES;
export type Params = Record<string, string | number>;

export const LANGUAGES: readonly { id: Lang; label: string }[] = [
  { id: 'ru', label: 'Русский' },
  { id: 'en', label: 'English' },
];

const STORAGE_KEY = 'reels.lang.v1';
const RUSSIAN_LIKE = new Set(['ru', 'be', 'kk']);

function fromTag(tag: string | null | undefined): Lang | null {
  if (!tag) return null;
  const base = tag.toLowerCase().split(/[-_]/)[0] ?? '';
  return base === '' ? null : RUSSIAN_LIKE.has(base) ? 'ru' : 'en';
}

function readStored(): Lang | null {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return value === 'ru' || value === 'en' ? value : null;
  } catch {
    return null;
  }
}

/** Язык системы: Telegram знает язык пользователя точнее, чем WebView. */
export function detectSystemLang(): Lang {
  if (typeof window === 'undefined') return 'ru';
  const telegram = (window as { Telegram?: { WebApp?: { initDataUnsafe?: { user?: { language_code?: string } } } } })
    .Telegram?.WebApp?.initDataUnsafe?.user?.language_code;
  return fromTag(telegram) ?? fromTag(typeof navigator === 'undefined' ? null : navigator.language) ?? 'en';
}

let current: Lang = typeof window === 'undefined' ? 'ru' : (readStored() ?? detectSystemLang());
const listeners = new Set<() => void>();

function applyDocument(): void {
  if (typeof document !== 'undefined') document.documentElement.lang = current;
}
applyDocument();

export function getLang(): Lang {
  return current;
}

/** Язык, который выбран явно в настройках; null — «как в системе». */
export function getLangPreference(): Lang | null {
  return readStored();
}

/** `null` сбрасывает выбор и возвращает язык системы. */
export function setLangPreference(lang: Lang | null): void {
  try {
    if (lang) window.localStorage.setItem(STORAGE_KEY, lang);
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // без хранилища выбор действует до закрытия приложения
  }
  const next = lang ?? detectSystemLang();
  if (next === current) return;
  current = next;
  applyDocument();
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Подписка компонента на смену языка. */
export function useLang(): Lang {
  return useSyncExternalStore(subscribe, getLang, getLang);
}

/** Локаль для дат и чисел. */
export function locale(): string {
  return current === 'ru' ? 'ru-RU' : 'en-US';
}

function interpolate(template: string, params?: Params): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (whole, name: string) => {
    const value = params[name];
    return value === undefined ? whole : String(value);
  });
}

/**
 * Строка по ключу. Для множественного числа передайте `n` в params:
 * форма выбирается по правилам текущего языка.
 */
export function t(key: MessageKey, params?: Params): string {
  const entry: Entry | undefined = MESSAGES[key];
  const value: string | Plural | undefined = entry?.[current];
  if (value === undefined) return key;
  if (typeof value === 'string') return interpolate(value, params);
  const n = Number(params?.['n'] ?? 0);
  const form = new Intl.PluralRules(locale()).select(n);
  return interpolate(value[form] ?? value.other, params);
}

/** Число в формате текущей локали (пробел-разделитель тысяч и т.п.). */
export function formatNumber(value: number): string {
  return value.toLocaleString(locale());
}

export function formatDate(value: Date | string | number, options?: Intl.DateTimeFormatOptions): string {
  return new Date(value).toLocaleDateString(locale(), options);
}

/**
 * Подписи из общего пакета @reels/shared написаны по-русски. Для английского
 * ищем перевод по русскому тексту; нет перевода — показываем как есть.
 */
export function ls(ru: string): string {
  if (current === 'ru') return ru;
  const key = `ls.${ru}` as MessageKey;
  const entry: Entry | undefined = MESSAGES[key];
  const value = entry?.en;
  return typeof value === 'string' ? value : ru;
}
